import React, { useState, useRef, useEffect} from 'react';
import {
  FiDownload,
  FiCalendar,
  FiFilter,
  FiX,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api';
import { showToast } from '../../utils/toast';

interface EodReportsProps {
  refresh?: () => void;
}

const EodReports: React.FC<EodReportsProps> = ({ refresh }) => {
  const today = new Date().toISOString().split('T')[0];
  const yesterday = (() => {
    const y = new Date();
    y.setDate(y.getDate() - 1);
    return y.toISOString().split('T')[0];
  })();

  const [selectedDate, setSelectedDate] = useState<string>(today);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedSection, setSelectedSection] = useState<'today' | 'yesterday' | 'custom' | null>(null);
  
  // Filter states
  const [filterStartDate, setFilterStartDate] = useState<string>('');
  const [filterEndDate, setFilterEndDate] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<'pending' | 'failed' | 'filled' | 'cancelled' | 'all'>('all');
  const [filterRole, setFilterRole] = useState<'master' | 'follower' | 'all'>('all');
  const [filterTradingType, setFilterTradingType] = useState<'swing' | 'day' | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 30;
  
  
  const dateInputRef = useRef<HTMLInputElement>(null);
  const filterStartDateRef = useRef<HTMLInputElement>(null);
  const filterEndDateRef = useRef<HTMLInputElement>(null);

  // Fetch counts for today and yesterday
  const { data: todayData, isLoading: todayLoading, error: todayError } = useQuery({
    queryKey: ['admin', 'eod-counts', 'today'],
    queryFn: () => adminApi.getEodCounts({ date: today }),
    retry: 1,
  });

  const { data: yesterdayData, isLoading: yesterdayLoading, error: yesterdayError } = useQuery({
    queryKey: ['admin', 'eod-counts', 'yesterday'],
    queryFn: () => adminApi.getEodCounts({ date: yesterday }),
    retry: 1,
  });

  // Fetch today's report data
  const { data: todayReportData, isLoading: todayReportsLoading, error: todayReportsError } = useQuery({
    queryKey: ['admin', 'eod-reports-data', 'today', today, filterRole, filterStatus, filterTradingType],
    queryFn: () => adminApi.getEodReportsData({
      startDate: today,
      endDate: today,
      role: filterRole !== 'all' ? filterRole : undefined,
      statusFilter: filterStatus !== 'all' ? filterStatus : undefined,
      swingTrader: filterTradingType === 'swing' ? true : undefined,
      dayTrader: filterTradingType === 'day' ? true : undefined,
    }),
    retry: 1,
  });

  // Fetch yesterday's report data
  const { data: yesterdayReportData, isLoading: yesterdayReportsLoading, error: yesterdayReportsError } = useQuery({
    queryKey: ['admin', 'eod-reports-data', 'yesterday', yesterday, filterRole, filterStatus, filterTradingType],
    queryFn: () => adminApi.getEodReportsData({
      startDate: yesterday,
      endDate: yesterday,
      role: filterRole !== 'all' ? filterRole : undefined,
      statusFilter: filterStatus !== 'all' ? filterStatus : undefined,
      swingTrader: filterTradingType === 'swing' ? true : undefined,
      dayTrader: filterTradingType === 'day' ? true : undefined,
    }),
    retry: 1,
  });

  // Fetch filtered date report data (when custom dates are selected and different from today/yesterday)
  const hasCustomDateFilter = Boolean(filterStartDate && filterEndDate && 
    (filterStartDate !== today || filterEndDate !== today) &&
    (filterStartDate !== yesterday || filterEndDate !== yesterday));
  
  const { data: customReportData, isLoading: customReportsLoading, error: customReportsError } = useQuery({
    queryKey: ['admin', 'eod-reports-data', 'custom', filterStartDate, filterEndDate, filterRole, filterStatus, filterTradingType],
    queryFn: () => adminApi.getEodReportsData({
      startDate: filterStartDate,
      endDate: filterEndDate,
      role: filterRole !== 'all' ? filterRole : undefined,
      statusFilter: filterStatus !== 'all' ? filterStatus : undefined,
      swingTrader: filterTradingType === 'swing' ? true : undefined,
      dayTrader: filterTradingType === 'day' ? true : undefined,
    }),
    enabled: hasCustomDateFilter,
    retry: 1,
  });

  const handleDateSelect = (type: 'today' | 'yesterday' | 'custom', customDate?: string) => {
    if (type === 'today') {
      setSelectedDate(today);
    } else if (type === 'yesterday') {
      const yesterdayMasterCount = yesterdayData?.masterCount || 0;
      const yesterdayFollowerCount = yesterdayData?.followerCount || 0;
      if (yesterdayMasterCount === 0 && yesterdayFollowerCount === 0) {
        showToast('info', 'No trade data available for yesterday. Please select a date with trades.');
        return;
      }
      setSelectedDate(yesterday);
    } else if (type === 'custom' && customDate) {
      const customDateObj = new Date(customDate);
      const todayDateObj = new Date(today);
      todayDateObj.setHours(23, 59, 59, 999);
      if (customDateObj > todayDateObj) {
        showToast('info', 'This is a future date. Please select today or a past date.');
        return;
      }
      setSelectedDate(customDate);
    }
  };

  const applyFilters = () => {
    if (filterStartDate && filterEndDate) {
      const startDateObj = new Date(filterStartDate);
      const endDateObj = new Date(filterEndDate);
      const todayDateObj = new Date(today);
      todayDateObj.setHours(23, 59, 59, 999);
      
      if (startDateObj > todayDateObj || endDateObj > todayDateObj) {
        showToast('info', 'Future dates are not allowed. Please select today or past dates.');
        return;
      }
      
      if (startDateObj > endDateObj) {
        showToast('info', 'Start date cannot be after end date.');
        return;
      }
      
      // Set custom section when filters are applied
      setSelectedSection('custom');
    }
    
    setShowFilterModal(false);
    setCurrentPage(1); // Reset to first page when filters are applied
  };

  // Reset to first page when search query or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterRole, filterStatus, filterTradingType, selectedSection]);

  const clearFilters = () => {
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterStatus('all');
    setFilterRole('all');
    setFilterTradingType('all');
    setSearchQuery('');
    setCurrentPage(1);
    setSelectedSection(null);
  };


  const openDatePicker = (ref: React.RefObject<HTMLInputElement>) => {
    if (ref.current) {
      if ('showPicker' in HTMLInputElement.prototype) {
        try {
          (ref.current as any).showPicker();
        } catch (e) {
          ref.current.click();
        }
      } else {
        ref.current.click();
      }
    }
  };

  const handleDownloadCurrentReports = async () => {
    try {
      setIsDownloading(true);
      
      // Determine date range based on current view
      let startDateToUse: string;
      let endDateToUse: string;
      
      if (hasCustomDateFilter && filterStartDate && filterEndDate) {
        // Custom date range
        startDateToUse = filterStartDate;
        endDateToUse = filterEndDate;
      } else if (selectedSection === 'yesterday') {
        // Yesterday's reports
        startDateToUse = yesterday;
        endDateToUse = yesterday;
      } else {
        // Today's reports (default)
        startDateToUse = today;
        endDateToUse = today;
      }
      
      const startDateObj = new Date(startDateToUse);
      const endDateObj = new Date(endDateToUse);
      const todayDateObj = new Date(today);
      todayDateObj.setHours(23, 59, 59, 999);
      
      if (startDateObj > todayDateObj || endDateObj > todayDateObj) {
        showToast('info', 'Future dates are not allowed. Please select today or past dates.');
        setIsDownloading(false);
        return;
      }
      
      if (startDateObj > endDateObj) {
        showToast('info', 'Start date cannot be after end date.');
        setIsDownloading(false);
        return;
      }
      
      console.log(`📥 [EOD Download] Downloading current screen reports with filters`);
      
      // Determine which roles are actually visible on screen
      // If search query is active, check what roles are shown after filtering
      let rolesToDownload: ('master' | 'follower')[] = [];
      
      if (filterRole !== 'all') {
        // Explicit role filter - download only that role
        rolesToDownload = [filterRole];
      } else {
        let currentData: any = null;
        if (hasCustomDateFilter && customReportData) {
          currentData = customReportData;
        } else if (selectedSection === 'yesterday' && yesterdayReportData) {
          currentData = yesterdayReportData;
        } else if (todayReportData) {
          currentData = todayReportData;
        }
        
        if (currentData && currentData.reports) {
          // Filter by search query if provided (same as screen filtering)
          let visibleReports = currentData.reports;
          if (searchQuery && searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            visibleReports = visibleReports.filter((report: any) => {
              if (!report || !report.user) return false;
              const userName = (report.user?.name || '').toLowerCase();
              const userEmail = (report.user?.email || '').toLowerCase();
              return userName.includes(query) || userEmail.includes(query);
            });
          }
          
          // Get unique roles from visible reports
          const visibleRoles = new Set<string>();
          visibleReports.forEach((report: any) => {
            if (report.user && report.user.role) {
              visibleRoles.add(report.user.role);
            }
          });
          
          // Only download roles that are visible on screen
          if (visibleRoles.has('master')) rolesToDownload.push('master');
          if (visibleRoles.has('follower')) rolesToDownload.push('follower');
          
          console.log(`👁️ [EOD Download] Visible roles on screen:`, Array.from(visibleRoles));
        } else {
          // No data available, use filterRole default behavior
          rolesToDownload = ['master', 'follower'];
        }
      }
      
      // If no roles found, don't download anything
      if (rolesToDownload.length === 0) {
        showToast('info', 'No data available to download.');
        setIsDownloading(false);
        return;
      }
      
      console.log(`📥 [EOD Download] Roles to download:`, rolesToDownload);
      
      let downloadSuccess = false;
      let downloadErrors: string[] = [];
      
      // Download each role separately
      for (const role of rolesToDownload) {
        try {
          // Prepare download parameters based on current screen filters
          const downloadParams: any = {
            startDate: startDateToUse,
            endDate: endDateToUse,
            role: role,
          };
          
          // Add status filter if not 'all'
          if (filterStatus && filterStatus !== 'all') {
            downloadParams.statusFilter = filterStatus;
          }
          
          // Add trading type filter if not 'all'
          if (filterTradingType === 'swing') {
            downloadParams.swingTrader = true;
          } else if (filterTradingType === 'day') {
            downloadParams.dayTrader = true;
          }
          
          // Add user search filter if provided (downloads only that user's reports)
          if (searchQuery && searchQuery.trim()) {
            downloadParams.userSearch = searchQuery.trim();
            console.log(`🔍 [EOD Download] Filtering by user search: "${searchQuery.trim()}"`);
          }
          
          console.log(`📥 [EOD Download] Downloading ${role} reports with params:`, JSON.stringify(downloadParams, null, 2));
          await adminApi.downloadEodReportsExcel(downloadParams);
          downloadSuccess = true;
        } catch (roleError: any) {
          console.error(`❌ [EOD Download] Error downloading ${role} reports:`, roleError);
          const roleErrorMessage = roleError.response?.data?.error || roleError.response?.data?.message || roleError.message || `Failed to download ${role} reports`;
          downloadErrors.push(`${role}: ${roleErrorMessage}`);
          // Continue with next role even if one fails
        }
      }
      
      if (downloadSuccess) {
        console.log(`✅ [EOD Download] Reports downloaded successfully`);
        if (downloadErrors.length > 0) {
          showToast('info', `Some reports downloaded. ${downloadErrors.join('; ')}`);
        } else {
          showToast('success', `Reports downloaded successfully`);
        }
      } else {
        // All downloads failed
        throw new Error(downloadErrors.join('; ') || 'Failed to download reports');
      }
    } catch (error: any) {
      console.error('❌ [EOD Download] Download error:', error);
      
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to download Excel report';
      
      const startDateToUse = filterStartDate || selectedDate;
      const startDateObj = new Date(startDateToUse);
      const todayDateObj = new Date(today);
      todayDateObj.setHours(23, 59, 59, 999);
      
      if (startDateObj > todayDateObj) {
        showToast('error', 'Future date selected. Please select today or a past date.');
      } else if (errorMessage.includes('No trades found') || errorMessage.includes('No data available') || errorMessage.includes('Empty report')) {
        const searchMsg = searchQuery && searchQuery.trim() 
          ? `No trade data found for "${searchQuery.trim()}" with the selected filters.`
          : 'No trade data available for the selected date range and filters.';
        showToast('info', searchMsg);
      } else {
        showToast('error', errorMessage);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'filled') return 'bg-green-100 text-green-800';
    if (statusLower === 'cancelled') return 'bg-red-100 text-red-800';
    if (['pending', 'new', 'accepted'].includes(statusLower)) return 'bg-yellow-100 text-yellow-800';
    return 'bg-[#d90429] text-white';
  };

  const getTypeColor = (type: string) => {
    const typeLower = type.toLowerCase();
    if (typeLower === 'buy') return 'bg-blue-100 text-blue-800';
    if (typeLower === 'sell') return 'bg-[#ff4d4f] text-white';
    return 'bg-gray-100 text-gray-800';
  };

  const formatDateDisplay = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const renderReportSection = (
    title: string,
    data: any,
    isLoading: boolean,
    error: any,
    dateStr: string
  ) => {
    // Filter reports by search query if provided
    let filteredReports = data?.reports || [];
    if (searchQuery && searchQuery.trim()) {
      try {
        const query = searchQuery.toLowerCase().trim();
        filteredReports = filteredReports.filter((report: any) => {
          if (!report || !report.user) return false;
          const userName = (report.user?.name || '').toLowerCase();
          const userEmail = (report.user?.email || '').toLowerCase();
          return userName.includes(query) || userEmail.includes(query);
        });
      } catch (error) {
        console.error('Error filtering reports by search:', error);
        // If filtering fails, show all reports
        filteredReports = data?.reports || [];
      }
    }
    
    const hasData = filteredReports.length > 0;
    const isFiltered = filterStatus !== 'all' || filterRole !== 'all' || searchQuery.trim() !== '';

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-xl font-bold text-foreground">{title}</h3>
          {isFiltered && (
            <span className="text-sm text-muted bg-primary-100 px-3 py-1 rounded-full">
              Filtered
            </span>
          )}
        </div>
        <div className="border-b-2 border-border mb-4"></div>

        {isLoading && (
          <div className="card p-6 text-center">
            <p className="text-muted">Loading reports...</p>
          </div>
        )}

        {error && (
          <div className="card p-6 bg-red-50 border border-red-200">
            <p className="text-red-800">Error loading reports. Please try again.</p>
          </div>
        )}

        {!isLoading && !error && !hasData && (
          <div className="card p-6 text-center">
            <p className="text-muted">
              {searchQuery.trim() 
                ? `No reports found for "${searchQuery}". Try a different search.`
                : 'Data not found or Reports not found'
              }
            </p>
          </div>
        )}

        {!isLoading && !error && hasData && (
          <div className="min-w-0">
            <div className="table-scroll">
            <table className="w-full min-w-[560px] border-collapse bg-surface">
              {/* Summary Row */}
              <thead>
                <tr className="bg-blue-600 text-white">
                  <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold">Total Trades</th>
                  <th className="border border-gray-300 px-3 py-2 text-center text-sm font-semibold">Filled</th>
                  <th className="border border-gray-300 px-3 py-2 text-center text-sm font-semibold">Cancel</th>
                  <th className="border border-gray-300 px-3 py-2 text-center text-sm font-semibold">Sell</th>
                  <th className="border border-gray-300 px-3 py-2 text-center text-sm font-semibold">Buy</th>
                  <th className="border border-gray-300 px-3 py-2 text-center text-sm font-semibold">Pending</th>
                  {(filteredReports.some((r: any) => r.user.role === 'follower') || filterRole === 'all') && (
                    <th className="border border-gray-300 px-3 py-2 text-center text-sm font-semibold">Failed</th>
                  )}
                </tr>
              </thead>
              <tbody>
                <tr className="bg-gray-100">
                  <td className="border border-gray-300 px-3 py-2 text-sm font-semibold text-center">
                    {filteredReports.reduce((sum: number, r: any) => sum + r.summary.totalTrades, 0)}
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm font-semibold text-center">
                    {filteredReports.reduce((sum: number, r: any) => sum + (r.summary.totalFilled || 0), 0)}
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm font-semibold text-center">
                    {filteredReports.reduce((sum: number, r: any) => sum + (r.summary.totalCancel || 0), 0)}
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm font-semibold text-center">
                    {filteredReports.reduce((sum: number, r: any) => sum + r.summary.sell, 0)}
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm font-semibold text-center">
                    {filteredReports.reduce((sum: number, r: any) => sum + r.summary.buy, 0)}
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm font-semibold text-center">
                    {filteredReports.reduce((sum: number, r: any) => sum + (r.summary.totalPending || 0), 0)}
                  </td>
                  {(filteredReports.some((r: any) => r.user.role === 'follower') || filterRole === 'all') && (
                    <td className="border border-gray-300 px-3 py-2 text-sm font-semibold text-center">
                      {filteredReports.reduce((sum: number, r: any) => sum + (r.summary.totalFailed || 0), 0)}
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
            </div>

            {/* Main Data Table */}
            <div className="mt-4 table-scroll">
              <table className="w-full min-w-[960px] border-collapse bg-surface">
                <thead>
                  <tr className="bg-blue-600 text-white">
                    <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold">Role</th>
                    <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold">Username</th>
                    <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold">Broker</th>
                    <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold">Order Name</th>
                    <th className="border border-gray-300 px-3 py-2 text-center text-sm font-semibold">Order Type</th>
                    <th className="border border-gray-300 px-3 py-2 text-center text-sm font-semibold">Type</th>
                    <th className="border border-gray-300 px-3 py-2 text-center text-sm font-semibold">Status</th>
                    <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold">Price</th>
                    <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold">Time</th>
                    {filteredReports.some((r: any) => r.trades.some((t: any) => t.masterFollowing)) && (
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold">Master Following</th>
                    )}
                    {(filteredReports.some((r: any) => r.user.role === 'follower') || filterRole === 'all') && (
                      <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold">Failed</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Flatten all trades with their report info and sort by date then time
                    const allTrades: Array<{trade: any, report: any, tradeIndex: number, reportIndex: number}> = [];
                    
                    filteredReports.forEach((report: any, reportIndex: number) => {
                      report.trades.forEach((trade: any, tradeIndex: number) => {
                        allTrades.push({ trade, report, tradeIndex, reportIndex });
                      });
                    });
                    
                    // Sort by date first, then by time
                    allTrades.sort((a, b) => {
                      // Use tradeDate and tradeTime if available, otherwise parse time string
                      let dateA: Date, dateB: Date;
                      
                      if (a.trade.tradeDate && a.trade.tradeTime) {
                        dateA = new Date(a.trade.tradeTime);
                      } else {
                        // Parse time string: "02/13/2026, 01:39:45"
                        const parts = a.trade.time.split(', ');
                        if (parts.length === 2) {
                          const datePart = parts[0]; // "02/13/2026"
                          const timePart = parts[1]; // "01:39:45"
                          const [month, day, year] = datePart.split('/');
                          const [hour, minute, second] = timePart.split(':');
                          dateA = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute), parseInt(second));
                        } else {
                          dateA = new Date(a.trade.time);
                        }
                      }
                      
                      if (b.trade.tradeDate && b.trade.tradeTime) {
                        dateB = new Date(b.trade.tradeTime);
                      } else {
                        const parts = b.trade.time.split(', ');
                        if (parts.length === 2) {
                          const datePart = parts[0];
                          const timePart = parts[1];
                          const [month, day, year] = datePart.split('/');
                          const [hour, minute, second] = timePart.split(':');
                          dateB = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute), parseInt(second));
                        } else {
                          dateB = new Date(b.trade.time);
                        }
                      }
                      
                      // Compare dates (YYYY-MM-DD)
                      const dateAStr = dateA.toISOString().split('T')[0];
                      const dateBStr = dateB.toISOString().split('T')[0];
                      
                      if (dateAStr !== dateBStr) {
                        return dateAStr.localeCompare(dateBStr); // Earlier date first
                      }
                      
                      // If same date, compare by time
                      return dateA.getTime() - dateB.getTime(); // Earlier time first
                    });
                    
                    const hasMasterFollowing = filteredReports.some((r: any) => r.trades.some((t: any) => t.masterFollowing));
                    const hasFailedColumn = filteredReports.some((r: any) => r.user.role === 'follower') || filterRole === 'all';
                    
                    // Calculate column count for pagination colspan
                    let columnCount = 9; // Base columns: Role, Username, Broker, Order Name, Order Type, Type, Status, Price, Time
                    if (hasMasterFollowing) columnCount++;
                    if (hasFailedColumn) columnCount++;
                    
                    // Pagination logic
                    const totalTrades = allTrades.length;
                    const totalPages = Math.ceil(totalTrades / itemsPerPage);
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const endIndex = startIndex + itemsPerPage;
                    const paginatedTrades = allTrades.slice(startIndex, endIndex);
                    
                    return (
                      <>
                        {paginatedTrades.map((item, index) => {
                      const { trade, report } = item;
                      const isFollower = report.user.role === 'follower';
                      const isMaster = report.user.role === 'master';
                      
                      return (
                        <tr 
                          key={`${item.reportIndex}-${item.tradeIndex}`}
                          className={`${isMaster ? 'bg-[#5c82a5] text-[#ffffff]' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                        >
                          <td className={`border border-gray-300 px-3 py-2 text-sm capitalize font-bold`}>
                            {report.user.role}
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-sm">{report.user.name}</td>
                          <td className="border border-gray-300 px-3 py-2 text-sm">{report.summary.broker}</td>
                          <td className="border border-gray-300 px-3 py-2 text-sm">
                          {trade.symbol} {((d)=>`${String(d.getDate()).padStart(2,'0')}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getHours()).padStart(2,'0')}${String(d.getMinutes()).padStart(2,'0')}`)(new Date(trade.filledAt || new Date()))}
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-sm text-center">{trade.orderType}</td>
                          <td className="border border-gray-300 px-3 py-2 text-sm text-center">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(trade.type)}`}>
                              {trade.type}
                            </span>
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-sm text-center">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(trade.status)}`}>
                              {trade.status}
                            </span>
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-sm">{trade.price}</td>
                          <td className="border border-gray-300 px-3 py-2 text-sm">{trade.time}</td>
                          {hasMasterFollowing && (
                            <td className="border border-gray-300 px-3 py-2 text-sm">{trade.masterFollowing || 'N/A'}</td>
                          )}
                          {hasFailedColumn && (
                            <td className="border border-gray-300 px-3 py-2 text-sm">
                              {isFollower && (trade.status.toLowerCase() === 'failed' || trade.status.toLowerCase() === 'cancelled') ? 'Yes' : isFollower ? 'No' : '-'}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                      {/* Pagination Controls */}
                      {totalPages > 1 && (
                        <tr>
                          <td colSpan={columnCount} className="border border-gray-300 px-4 py-4 bg-gray-50">
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
                              <div className="text-sm text-gray-700">
                                Showing {startIndex + 1} to {Math.min(endIndex, totalTrades)} of {totalTrades} trades
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                  disabled={currentPage === 1}
                                  className={`px-3 py-1.5 rounded-lg border transition-colors ${
                                    currentPage === 1
                                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed border-gray-300'
                                      : 'bg-white text-gray-700 hover:bg-gray-100 border-gray-300'
                                  }`}
                                >
                                  <FiChevronLeft className="h-4 w-4" />
                                </button>
                                
                                {/* Page Numbers */}
                                <div className="flex flex-wrap items-center gap-1">
                                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                    let pageNum: number;
                                    if (totalPages <= 5) {
                                      pageNum = i + 1;
                                    } else if (currentPage <= 3) {
                                      pageNum = i + 1;
                                    } else if (currentPage >= totalPages - 2) {
                                      pageNum = totalPages - 4 + i;
                                    } else {
                                      pageNum = currentPage - 2 + i;
                                    }
                                    
                                    return (
                                      <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`px-3 py-1.5 rounded-lg border transition-colors ${
                                          currentPage === pageNum
                                            ? 'bg-primary-600 text-white border-primary-600'
                                            : 'bg-white text-gray-700 hover:bg-gray-100 border-gray-300'
                                        }`}
                                      >
                                        {pageNum}
                                      </button>
                                    );
                                  })}
                                </div>
                                
                                <button
                                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                  disabled={currentPage === totalPages}
                                  className={`px-3 py-1.5 rounded-lg border transition-colors ${
                                    currentPage === totalPages
                                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed border-gray-300'
                                      : 'bg-white text-gray-700 hover:bg-gray-100 border-gray-300'
                                  }`}
                                >
                                  <FiChevronRight className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const todayMasterCount = todayData?.masterCount || 0;
  const todayFollowerCount = todayData?.followerCount || 0;
  const yesterdayMasterCount = yesterdayData?.masterCount || 0;
  const yesterdayFollowerCount = yesterdayData?.followerCount || 0;

  if (todayError || yesterdayError) {
    console.error('EOD Reports errors:', { todayError, yesterdayError });
  }

  return (
    <div className="space-y-6 animate-fade-in relative">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="lg:text-4xl text-2xl title-page">
            EOD Reports
          </h2>
          <p className="text-muted lg:mt-2 mt-1 lg:text-lg text-[14px]">
            Download detailed trade reports for masters and followers
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:flex-wrap w-full sm:w-auto">
          {/* Search Bar */}
          <div className="relative w-full sm:w-auto">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted h-5 w-5" />
            <input
              type="text"
              placeholder="Search by username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-surface border border-border rounded-lg text-foreground placeholder-muted focus:outline-none focus:ring-2 focus:ring-primary w-full sm:w-64"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted hover:text-foreground"
              >
                <FiX className="h-4 w-4" />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilterModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-colors"
          >
            <FiFilter className="h-5 w-5" />
            <span>Reports Filter</span>
          </button>
        </div>
      </div>

      {/* Download reports */}
      <div className="flex flex-col items-end space-y-2">
        <p className="text-sm text-muted text-right">
          {filterStartDate && filterEndDate 
            ? `Date Range: ${filterStartDate} to ${filterEndDate}`
            : filterStartDate
            ? `Date: ${filterStartDate}`
            : selectedSection === 'yesterday'
            ? `Date: ${yesterday}`
            : `Date: ${today}`}
        </p>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            onClick={handleDownloadCurrentReports}
            disabled={isDownloading}
            className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiDownload className="h-5 w-5" />
            <span>Download Reports</span>
          </button>
        </div>
      </div>

      {/* Reports Display */}
      <div className="space-y-8">
        {/* Today Section - Show when today button is clicked or no section selected */}
        {(selectedSection === 'today' || (!selectedSection && !hasCustomDateFilter)) && renderReportSection(
          'Today',
          todayReportData,
          todayReportsLoading,
          todayReportsError,
          today
        )}

        {/* Yesterday Section - Show when yesterday button is clicked */}
        {selectedSection === 'yesterday' && renderReportSection(
          'Yesterday',
          yesterdayReportData,
          yesterdayReportsLoading,
          yesterdayReportsError,
          yesterday
        )}

        {/* Custom Date Range Section - Show when filters are applied */}
        {hasCustomDateFilter && selectedSection === 'custom' && renderReportSection(
          filterStartDate === filterEndDate 
            ? formatDateDisplay(filterStartDate)
            : `${formatDateDisplay(filterStartDate)} - ${formatDateDisplay(filterEndDate)}`,
          customReportData,
          customReportsLoading,
          customReportsError,
          filterStartDate
        )}
      </div>

      {/* Advanced Filter Modal */}
      {showFilterModal && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowFilterModal(false)}
          ></div>
          
          {/* Modal - Right Side */}
          <div className="fixed right-0 top-0 h-full w-full max-w-md bg-surface border-l border-border shadow-xl z-50 overflow-y-auto">
            <div className="p-4 sm:p-6 space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-border pb-4">
                <h3 className="text-xl font-bold text-foreground">Reports Filter</h3>
                <button
                  onClick={() => setShowFilterModal(false)}
                  className="p-2 hover:bg-surface-elevated rounded-lg transition-colors"
                >
                  <FiX className="h-5 w-5 text-muted" />
                </button>
              </div>
               {/* Today/Yesterday Buttons */}
               <div className="pt-2 border-border space-y-3">
                <h4 className="text-md font-semibold text-foreground">View Reports</h4>
                <button
                  onClick={() => {
                    clearFilters();
                    setSelectedSection('today');
                    setShowFilterModal(false);
                  }}
                  className={`w-full px-4 py-3 rounded-lg font-semibold transition-colors ${
                    selectedSection === 'today'
                      ? 'bg-primary-600 text-white'
                      : 'bg-surface border border-border text-foreground hover:bg-surface-elevated'
                  }`}
                >
                  Today
                </button>
                <button
                  onClick={() => {
                    clearFilters();
                    setSelectedSection('yesterday');
                    setShowFilterModal(false);
                    
                    // Check if yesterday has reports after a short delay to allow query to complete
                    setTimeout(() => {
                      if (yesterdayReportData && (!yesterdayReportData.reports || yesterdayReportData.reports.length === 0)) {
                        showToast('info', 'No record found please select another date');
                      }
                    }, 500);
                  }}
                  className={`w-full px-4 py-3 rounded-lg font-semibold transition-colors ${
                    selectedSection === 'yesterday'
                      ? 'bg-primary-600 text-white'
                      : 'bg-surface border border-border text-foreground hover:bg-surface-elevated'
                  }`}
                >
                  Yesterday
                </button>
              </div>

              {/* Filters */}
              <div className="space-y-4">
                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Start Date</label>
                  <div className="relative">
                    <FiCalendar 
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted h-5 w-5 z-20 cursor-pointer hover:text-primary transition-colors" 
                      onClick={(e) => {
                        e.stopPropagation();
                        openDatePicker(filterStartDateRef);
                      }}
                    />
                    <input
                      ref={filterStartDateRef}
                      type="date"
                      value={filterStartDate}
                      onChange={(e) => {
                        const newStartDate = e.target.value;
                        setFilterStartDate(newStartDate);
                        // Automatically set end date to start date
                        setFilterEndDate(newStartDate);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        openDatePicker(filterStartDateRef);
                      }}
                      onFocus={(e) => {
                        e.stopPropagation();
                        openDatePicker(filterStartDateRef);
                      }}
                      max={today}
                      className="w-full pl-10 pr-4 py-2 bg-surface border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
                    />
                  </div>
                </div>

                {/* End Date */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">End Date</label>
                  <div className="relative">
                    <FiCalendar 
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted h-5 w-5 z-20 cursor-pointer hover:text-primary transition-colors" 
                      onClick={(e) => {
                        e.stopPropagation();
                        openDatePicker(filterEndDateRef);
                      }}
                    />
                    <input
                      ref={filterEndDateRef}
                      type="date"
                      value={filterEndDate}
                      onChange={(e) => setFilterEndDate(e.target.value)}
                      onClick={(e) => {
                        e.stopPropagation();
                        openDatePicker(filterEndDateRef);
                      }}
                      onFocus={(e) => {
                        e.stopPropagation();
                        openDatePicker(filterEndDateRef);
                      }}
                      max={today}
                      className="w-full pl-10 pr-4 py-2 bg-surface border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
                    />
                  </div>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Status Filter</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value as 'pending' | 'failed' | 'filled' | 'cancelled' | 'all')}
                    className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                    <option value="filled">Filled</option>
                    <option value="cancelled">Cancel</option>
                  </select>
                </div>
                {/* Trading Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Trading Type</label>
                  <select
                    value={filterTradingType}
                    onChange={(e) => setFilterTradingType(e.target.value as 'swing' | 'day' | 'all')}
                    className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="all">All Traders</option>
                    <option value="swing">Swing Trader</option>
                    <option value="day">Day Trader</option>
                  </select>
                </div>

                {/* Role Filter */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Role Filter</label>
                  <select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value as 'master' | 'follower' | 'all')}
                    className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="all">All Roles</option>
                    <option value="master">Master Only</option>
                    <option value="follower">Follower Only</option>
                  </select>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2 border-border">
                <button
                  onClick={clearFilters}
                  className="flex-1 px-4 py-2 bg-surface border border-border text-foreground rounded-lg font-semibold hover:bg-surface-elevated transition-colors"
                >
                  Clear
                </button>
                <button
                  onClick={applyFilters}
                  className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EodReports;
